var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var _a;
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import { useTranslation } from "react-i18next";
import { Footer, Header, PageBanner } from "@sequentech/ui-essentials";
import { EVotingPortalCountdownPolicy, USER_LANGUAGE_COOKIE_NAME, setCookie, getValueFromCookie, sanitizePresentationCss, } from "@sequentech/ui-core";
import SequentLogo from "@sequentech/ui-essentials/public/Sequent_logo.svg";
import BlankLogoImg from "@sequentech/ui-essentials/public/blank_logo.svg";
import { AuthContext, ContactChangeOutcome, } from "../providers/AuthContextProvider";
import { SettingsContext } from "../providers/SettingsContextProvider";
import { useAppSelector } from "../store/hooks";
import { selectElectionIds } from "../store/elections/electionsSlice";
import { selectBallotStyleByElectionId, selectBallotStyleElectionIds, selectFirstBallotStyle, } from "../store/ballotStyles/ballotStylesSlice";
import { selectElectionEventById } from "../store/electionEvents/electionEventsSlice";
import WatermarkBackground from "./WaterMark/Watermark";
import { BallotSelectionAdapter } from "./BallotSelectionAdapter";
import { ScreenAudioInstructions } from "./ScreenAudioInstructions/ScreenAudioInstructions";
import { useElectionClassName } from "../hooks/useElectionClassName";
var StyledApp = styled(Stack)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    min-height: 100vh;\n\n    /* Visually hidden until focused, then shown for keyboard users */\n    .skip-link {\n        position: absolute;\n        top: -40px;\n        left: 0;\n        background: #fff;\n        color: #000;\n        padding: 8px 12px;\n        z-index: 1000;\n        text-decoration: none;\n    }\n    .skip-link:focus {\n        top: 0;\n    }\n"], ["\n    min-height: 100vh;\n\n    /* Visually hidden until focused, then shown for keyboard users */\n    .skip-link {\n        position: absolute;\n        top: -40px;\n        left: 0;\n        background: #fff;\n        color: #000;\n        padding: 8px 12px;\n        z-index: 1000;\n        text-decoration: none;\n    }\n    .skip-link:focus {\n        top: 0;\n    }\n"])));
var StyledAppWrapper = styled(Stack)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    ", "\n"], ["\n    ", "\n"])), function (_a) {
    var customCss = _a.customCss;
    return customCss;
});
var OUTCOME_NOTICES = (_a = {},
    _a[ContactChangeOutcome.Success] = { key: "contactChannel.updated", severity: "success" },
    _a[ContactChangeOutcome.Cancelled] = { key: "contactChannel.cancelled", severity: "info" },
    _a[ContactChangeOutcome.Error] = { key: "contactChannel.failed", severity: "error" },
    _a);
/**
 * How the contact change ended, when the voter comes back from Keycloak. It stays until the voter
 * closes it, so it can be read at any pace.
 */
var ContactChangeNotice = function () {
    var t = useTranslation().t;
    var _a = useContext(AuthContext), contactChangeOutcome = _a.contactChangeOutcome, clearContactChangeOutcome = _a.clearContactChangeOutcome;
    if (contactChangeOutcome === undefined) {
        return null;
    }
    var notice = OUTCOME_NOTICES[contactChangeOutcome];
    return (_jsx(Snackbar, { open: true, className: "contact-change-notice", anchorOrigin: { vertical: "top", horizontal: "center" }, onClose: function (_event, reason) {
            // Only the voter's own close dismisses it, never a click elsewhere.
            if (reason !== "clickaway") {
                clearContactChangeOutcome === null || clearContactChangeOutcome === void 0 ? void 0 : clearContactChangeOutcome();
            }
        }, children: _jsx(Alert, { className: "contact-change-alert", severity: notice.severity, onClose: function () { return clearContactChangeOutcome === null || clearContactChangeOutcome === void 0 ? void 0 : clearContactChangeOutcome(); }, closeText: t("contactChannel.close"), children: t(notice.key) }) }));
};
var HeaderWithContext = function () {
    var _a, _b, _c, _d, _e, _f;
    var t = useTranslation().t;
    // Offered only to a signed-in voter, when the event has a channel for codes. A context
    // without these (one that can't reach Keycloak, such as the embedded preview) offers none.
    var authContext = useContext(AuthContext);
    var changeContactChannel = authContext.changeContactChannel;
    var showContactChange = authContext.isAuthenticated &&
        authContext.contactChangeAvailable === true &&
        changeContactChannel !== undefined;
    var globalSettings = useContext(SettingsContext).globalSettings;
    var eventId = useParams().eventId;
    var ballotStyle = useAppSelector(selectFirstBallotStyle);
    var electionEvent = useAppSelector(selectElectionEventById(eventId));
    var presentation = (_b = (_a = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml.election_event_presentation) !== null && _a !== void 0 ? _a : electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation) !== null && _b !== void 0 ? _b : undefined;
    var languagesList = (_d = (_c = presentation === null || presentation === void 0 ? void 0 : presentation.language_conf) === null || _c === void 0 ? void 0 : _c.enabled_language_codes) !== null && _d !== void 0 ? _d : ["en"];
    var showUserProfile = (_e = presentation === null || presentation === void 0 ? void 0 : presentation.show_user_profile) !== null && _e !== void 0 ? _e : true;
    var countdownPolicy = useMemo(function () {
        return presentation === null || presentation === void 0 ? void 0 : presentation.voting_portal_countdown_policy;
    }, [presentation]);
    var logoImg = (presentation === null || presentation === void 0 ? void 0 : presentation.logo_url) === undefined
        ? BlankLogoImg
        : (presentation === null || presentation === void 0 ? void 0 : presentation.logo_url) === null
            ? SequentLogo
            : presentation === null || presentation === void 0 ? void 0 : presentation.logo_url;
    var onChangeLanguage = function (lang) {
        if (getValueFromCookie(USER_LANGUAGE_COOKIE_NAME) !== lang) {
            setCookie(USER_LANGUAGE_COOKIE_NAME, lang);
        }
    };
    return (_jsx(Header, { appVersion: { main: globalSettings.APP_VERSION }, appHash: { main: globalSettings.APP_HASH }, userProfile: {
            firstName: authContext.firstName,
            username: authContext.username,
            email: authContext.email,
            openLink: showUserProfile ? authContext.openProfileLink : undefined,
            menuActions: showContactChange
                ? [
                    {
                        id: "contact-method",
                        label: t("contactChannel.change"),
                        icon: (_jsx(ContactPhoneOutlinedIcon, { className: "contact-method-icon", "aria-hidden": true })),
                        onSelect: function () { return void changeContactChannel(); },
                    },
                ]
                : undefined,
        }, languagesList: languagesList, logoutFn: authContext.isAuthenticated ? authContext.logout : undefined, logoUrl: logoImg, expiry: {
            alertAt: countdownPolicy === null || countdownPolicy === void 0 ? void 0 : countdownPolicy.countdown_alert_anticipation_secs,
            countdown: (_f = countdownPolicy === null || countdownPolicy === void 0 ? void 0 : countdownPolicy.policy) !== null && _f !== void 0 ? _f : EVotingPortalCountdownPolicy.NO_COUNTDOWN,
            countdownAt: countdownPolicy === null || countdownPolicy === void 0 ? void 0 : countdownPolicy.countdown_anticipation_secs,
            endTime: authContext.getExpiry(),
            duration: countdownPolicy === null || countdownPolicy === void 0 ? void 0 : countdownPolicy.countdown_anticipation_secs,
        }, onChangeLanguage: onChangeLanguage, accessibilitySettingsPolicy: presentation === null || presentation === void 0 ? void 0 : presentation.voter_accessibility_settings_policy }));
};
var Unwrapped = function (_a) {
    var children = _a.children;
    return _jsx(_Fragment, { children: children });
};
/**
 * What surrounds every voter screen: the event's stylesheet, the header with its logo and
 * languages, the demo watermark and the footer. The portal's application route and the
 * embedded voter preview both render the screens inside it.
 */
export var PortalChrome = function (_a) {
    var _b, _c;
    var _d = _a.Wrapper, Wrapper = _d === void 0 ? Unwrapped : _d, before = _a.before, children = _a.children;
    var t = useTranslation().t;
    var globalSettings = useContext(SettingsContext).globalSettings;
    var eventId = useParams().eventId;
    var electionEvent = useAppSelector(selectElectionEventById(eventId));
    var electionIds = useAppSelector(selectElectionIds);
    var ballotStyleElectionIds = useAppSelector(selectBallotStyleElectionIds);
    var ballotStyle = useAppSelector(function (state) {
        var _a;
        var electionId = (_a = electionIds[0]) !== null && _a !== void 0 ? _a : ballotStyleElectionIds[0];
        return electionId ? selectBallotStyleByElectionId(String(electionId))(state) : undefined;
    });
    var presentation = (_b = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml.election_event_presentation) !== null && _b !== void 0 ? _b : electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation;
    var css = (_c = presentation === null || presentation === void 0 ? void 0 : presentation.css) !== null && _c !== void 0 ? _c : "";
    var customCss = useMemo(function () {
        return sanitizePresentationCss(css, {
            baseUrl: document.baseURI,
            publicBucketUrl: globalSettings.PUBLIC_BUCKET_URL,
        });
    }, [css, globalSettings.PUBLIC_BUCKET_URL]);
    useElectionClassName();
    return (_jsx(StyledAppWrapper, { className: "voting-portal-wrapper", customCss: customCss, children: _jsxs(StyledApp, { className: "voting-portal app-root", children: [before, _jsx("nav", { className: "skip-navigation", "aria-label": t("a11y.skipToContent"), children: _jsx("a", { className: "skip-link", href: "#main-content", children: t("a11y.skipToContent") }) }), _jsxs(Wrapper, { children: [_jsx(HeaderWithContext, {}), _jsx(ContactChangeNotice, {}), _jsxs(PageBanner, { marginBottom: "auto", sx: {
                                display: "flex",
                                position: "relative",
                                flex: 1,
                                justifyContent: "flex-start",
                            }, className: "main", component: "main", id: "main-content", tabIndex: -1, children: [_jsx(WatermarkBackground, {}), _jsx(ScreenAudioInstructions, {}), _jsx(BallotSelectionAdapter, { children: children })] })] }), _jsx(Footer, {})] }) }));
};
var templateObject_1, templateObject_2;
